import { api } from './index';
import { MEMBERS_TABLE_TAG } from './membersTableTag';

import type { CoachStatus } from '@/constants/coachStatus';
import type { CoachSortBy } from '@/constants/coaches';
import type {
  CoachProfile,
  PaginatedResponse,
  Role,
  SeatType,
  SortDirection,
  User,
  UserAssesment,
  UserContractEnd,
  UserEmployment,
  UserNotifications,
  UserProfile,
  UsersEngagement,
} from '@/types';
import type {
  MemberSeatChangesResponse,
  SeatChangePreview,
  SeatChangeReview,
} from '@/types/memberSeatChange';

import { isSeatChangePreview } from '@/types/seatChangePreview';
import { redirectToSignin } from '@/utils/authRedirect';

export interface UserApiOptions {
  capacityMax?: number;
  capacityMin?: number;
  coach?: boolean;
  coachStatuses?: CoachStatus[];
  filter?: string;
  limit?: number;
  membersMax?: number;
  membersMin?: number;
  page?: number;
  search?: string;
  sortBy?: string;
  sortDirection?: SortDirection;
  userId?: string;
  view?: string;
}

export interface ManageCoach extends User {
  coachStatus: CoachStatus | null;
  coachStatuses?: CoachStatus[];
}

export interface ManageCoachesApiOptions extends Omit<
  UserApiOptions,
  'sortBy' | 'view'
> {
  sortBy?: CoachSortBy;
}

export interface CoachStatusResponse {
  coachStatus: { state: CoachStatus; states: CoachStatus[] } | null;
}

export interface CoachStatusApiError {
  message: string;
  status?: number | string;
}

const coachStatusError = ({
  data,
  status,
}: {
  data?: unknown;
  status?: number | string;
}): CoachStatusApiError => {
  if (status === 422 && data && typeof data === 'object') {
    const { errors } = data as { errors?: unknown };
    if (errors && typeof errors === 'object') {
      const { state, states } = errors as {
        state?: unknown;
        states?: unknown;
      };
      const stateErrors = Array.isArray(states) ? states : state;
      if (Array.isArray(stateErrors) && typeof stateErrors[0] === 'string') {
        return { message: stateErrors[0], status };
      }
    }
  }

  return { message: 'Unable to update status. Please try again.', status };
};

export interface CoachProfilesApiOptions {
  limit?: number;
  page?: number;
  search?: string;
}

export type MembersTableSortBy =
  | 'name'
  | 'company'
  | 'email'
  | 'joined_at'
  | 'contract_end_date'
  | 'end_date_status'
  | 'coach_name'
  | 'member_status'
  | 'seat_type'
  | 'admin_note'
  | 'growth_plan_started'
  | 'insights_status';

export type MembersTableSortDirection = 'asc' | 'desc';

export type MembersTableCoachCountFilter =
  'none' | 'one' | 'two' | 'three_plus';

export interface MembersTableParams {
  businessPlans?: string[];
  chemCall?: boolean;
  coachIds?: string[];
  coachesAssigned?: MembersTableCoachCountFilter[];
  cohortTagIds?: string[];
  companyIds?: string[];
  companyMemberStatus?: 'current' | 'past';
  endDateStatuses?: string[];
  growthPlanStarted?: string[];
  insightsStatuses?: string[];
  limit?: number;
  memberStatuses?: string[];
  noUpcomingSessions?: boolean;
  page?: number;
  scheduledSessions?: boolean;
  search?: string;
  seatTypes?: SeatType[];
  sortBy?: MembersTableSortBy;
  sortDirection?: MembersTableSortDirection;
}

export interface MembersTableFacetOption {
  id: string;
  name: string;
}

export interface MembersTableFacets {
  businessPlans: MembersTableFacetOption[];
  coaches: MembersTableFacetOption[];
  coachesAssigned: MembersTableFacetOption[];
  companies: MembersTableFacetOption[];
  endDateStatuses: string[];
  growthPlanStarted: MembersTableFacetOption[];
  insightsStatuses: MembersTableFacetOption[];
  memberStatuses: MembersTableFacetOption[];
  seatTypes: MembersTableFacetOption[];
  sessionFilters: MembersTableFacetOption[];
}

export interface MembersTableRowUserProfile {
  experience?: string | null;
  seatType?: SeatType | null;
  timezone?: string | null;
  title?: string | null;
}

export interface MembersTableRowUser {
  avatarUrl?: string | null;
  createdAt: string;
  email: string;
  firstName: string;
  fullName: string;
  id: string;
  lastName: string;
  profile?: MembersTableRowUserProfile | null;
}

export interface MembersTableRowCompany {
  id: string;
  name: string;
}

export interface MembersTableRowCoach {
  avatarUrl?: string | null;
  id: string;
  name: string;
}

export type MembersTableSessionType = 'biweekly' | 'chemistry' | 'single';

export interface MembersTableUpcomingSession {
  coach?: MembersTableRowCoach;
  startedAt: string;
  type: MembersTableSessionType;
}

export interface MembersTableRowSessions {
  chemCalls: number;
  completed: number;
  nextSessionAt: string | null;
  upcoming: number;
  upcomingSessions?: MembersTableUpcomingSession[];
}

export interface MembersTableRowPause {
  datePaused: string | null;
  returnDate: string | null;
}

export interface MembersTableApiRow {
  adminNote?: string | null;
  coaches: MembersTableRowCoach[];
  cohortTags?: { id: string; name: string }[];
  company: MembersTableRowCompany | null;
  contractEnd: UserContractEnd | null;
  growthPlanStarted?: boolean;
  insightsStatus?: 'unsent' | 'in_progress' | 'shared' | 'unknown';
  joinedAt: string;
  memberStatus: string | null;
  pause?: MembersTableRowPause | null;
  sessions: MembersTableRowSessions;
  user: MembersTableRowUser;
}

export interface MembersTableResponse {
  facets: MembersTableFacets;
  items: MembersTableApiRow[];
  pages: number;
  total: number;
}

export { MEMBERS_TABLE_TAG };

const membersTableParams = ({
  businessPlans,
  chemCall,
  coachIds,
  coachesAssigned,
  cohortTagIds,
  companyIds,
  companyMemberStatus,
  endDateStatuses,
  growthPlanStarted,
  insightsStatuses,
  limit = 50,
  memberStatuses,
  noUpcomingSessions,
  page = 1,
  scheduledSessions,
  search,
  seatTypes,
  sortBy,
  sortDirection,
}: MembersTableParams) => ({
  business_plans: businessPlans,
  chem_call: chemCall,
  coach_ids: coachIds,
  coaches_assigned: coachesAssigned,
  cohort_tag_ids: cohortTagIds,
  company_ids: companyIds,
  company_member_status: companyMemberStatus,
  end_date_statuses: endDateStatuses,
  growth_plan_started: growthPlanStarted,
  insights_statuses: insightsStatuses,
  limit,
  member_statuses: memberStatuses,
  no_upcoming_sessions: noUpcomingSessions,
  page,
  scheduled_sessions: scheduledSessions,
  search,
  seat_types: seatTypes,
  sort_by: sortBy,
  sort_direction: sortDirection,
  view: 'members_table',
});

const appliedSeatProgress = new WeakMap<object, Map<string, number>>();

export const userApi = api.injectEndpoints({
  endpoints: (builder) => ({
    previewMemberSeatChange: builder.mutation<
      SeatChangePreview,
      { id: string; targetSeatType: SeatType }
    >({
      async queryFn({ id, targetSeatType }, _api, _options, baseQuery) {
        const target =
          targetSeatType === 'standard_leadership'
            ? 'leadership'
            : targetSeatType;
        const result = await baseQuery({
          data: {
            seatChange: {
              targetSeatType: target,
            },
          },
          method: 'POST',
          url: `/user_profiles/${id}/seat_changes/preview`,
        });
        if (result.error) return { error: result.error };
        if (
          !isSeatChangePreview(result.data) ||
          result.data.memberLegacyId !== id ||
          result.data.targetSeatType !== target
        )
          return {
            error: {
              status: 503,
              data: {
                code: 'seat_change_preview_unavailable',
                message:
                  'Calendar review is unavailable. Request a fresh review.',
              },
            },
          };
        return { data: result.data };
      },
    }),
    getMemberSeatChanges: builder.query<MemberSeatChangesResponse, string>({
      providesTags: (_result, _error, id) => [{ id, type: 'User' }],
      query: (id) => ({
        method: 'GET',
        url: `/user_profiles/${id}/seat_changes`,
      }),
      async onQueryStarted(_id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const versions =
            appliedSeatProgress.get(dispatch) ?? new Map<string, number>();
          appliedSeatProgress.set(dispatch, versions);
          let changed = false;
          for (const operation of data.operations) {
            if (
              operation.appliedProgressVersion >
              (versions.get(operation.id) ?? 0)
            ) {
              versions.set(operation.id, operation.appliedProgressVersion);
              changed = true;
            }
          }
          if (changed) dispatch(api.util.invalidateTags(['Session']));
        } catch {
          // Status errors are rendered by the query consumer; never invalidate on failure.
        }
      },
    }),
    getCurrentUser: builder.query<User, void>({
      providesTags: ['User'],
      query: () => ({ method: 'GET', url: '/users/me' }),
      transformResponse: (response: User | null) =>
        // TODO: Move identify logic out of API call
        // identify(response);
        response || ({} as User),
      transformErrorResponse: (response: {
        status?: number;
        data?: unknown;
      }) => {
        if (response.status === 401) {
          const currentPath = window.location.pathname + window.location.search;
          redirectToSignin(currentPath);
        }
        return response;
      },
    }),

    getCurrentUserSessionProbe: builder.query<User, void>({
      query: () => ({ method: 'GET', url: '/users/me' }),
      transformResponse: (response: User | null) => response || ({} as User),
    }),

    getUser: builder.query<User, string>({
      providesTags: (_result, _error, id) => [{ id, type: 'User' }],
      query: (id) => ({ method: 'GET', url: `/users/${id}` }),
    }),

    getUsers: builder.query<PaginatedResponse<User>, UserApiOptions>({
      providesTags: ['User'],
      query: (params) => ({
        method: 'GET',
        params,
        url: '/users',
      }),
    }),

    getManageCoaches: builder.query<
      PaginatedResponse<ManageCoach>,
      ManageCoachesApiOptions
    >({
      providesTags: [{ id: 'MANAGE_COACHES', type: 'User' }],
      query: (params) => ({
        method: 'GET',
        params: {
          ...params,
          coachStatusContract: 'canonical_v1',
          view: 'manage_coaches',
        },
        url: '/users',
      }),
    }),

    getCoachStatus: builder.query<CoachStatusResponse, string>({
      providesTags: (_result, _error, id) => [{ id, type: 'CoachStatus' }],
      query: (id) => ({ method: 'GET', url: `/users/${id}/coach_status` }),
    }),

    updateCoachStatus: builder.mutation<
      CoachStatusResponse,
      { id: string; states: CoachStatus[] }
    >({
      invalidatesTags: (result, _error, { id }) =>
        result
          ? [
              { id: 'MANAGE_COACHES', type: 'User' },
              { id, type: 'User' },
              { id, type: 'CoachStatus' },
            ]
          : [],
      query: ({ id, states }) => ({
        data: { coachStatus: { states } },
        method: 'PUT',
        url: `/users/${id}/coach_status`,
      }),
      transformErrorResponse: coachStatusError,
    }),

    getMembersTable: builder.query<MembersTableResponse, MembersTableParams>({
      providesTags: [MEMBERS_TABLE_TAG],
      query: (params) => ({
        method: 'GET',
        params: membersTableParams(params),
        url: '/users',
      }),
    }),

    getUsersEngagement: builder.query<
      UsersEngagement,
      { companyId: string; start?: Date }
    >({
      providesTags: ['User'],
      query: ({ companyId, start }) => {
        const startDate = start ? start.toISOString().split('T')[0] : '';
        return {
          method: 'GET',
          url: `/users/stats?company_id=${companyId}${startDate ? `&start=${startDate}` : ''}`,
        };
      },
    }),

    updateUser: builder.mutation<User, { id: string; user: Partial<User> }>({
      invalidatesTags: (_result, _error, { id }) => [{ id, type: 'User' }],
      query: ({ id, user }) => ({
        data: { user },
        method: 'PUT',
        url: `/users/${id}`,
      }),
    }),

    updateUserTranscriptSharing: builder.mutation<
      User,
      { id: string; enabled: boolean }
    >({
      invalidatesTags: (_result, _error, { id }) => [{ id, type: 'User' }],
      query: ({ id, enabled }) => ({
        data: { enabled },
        method: 'PATCH',
        url: `/users/${id}/transcript_sharing`,
      }),
    }),

    updateUserB2BEnabled: builder.mutation<
      Pick<User, 'id' | 'b2bEnabled' | 'b2b_enabled'>,
      { id: string; enabled: boolean }
    >({
      invalidatesTags: (_result, _error, { id }) => [{ id, type: 'User' }],
      query: ({ id, enabled }) => ({
        data: { b2b_enabled: enabled },
        method: 'PATCH',
        url: `/users/${id}/b2b_enabled`,
      }),
    }),

    // Get coach profiles
    getCoachProfiles: builder.query<CoachProfile[], CoachProfilesApiOptions>({
      providesTags: ['User'],
      query: ({ limit = 20, page = 1, search = '' } = {}) => ({
        method: 'GET',
        params: { limit, page, search },
        url: '/coach_profiles',
      }),
    }),

    // User Profile operations
    updateUserProfile: builder.mutation<
      UserProfile,
      {
        id: string;
        userProfile: Partial<UserProfile>;
        seatChangeReview?: SeatChangeReview;
      }
    >({
      invalidatesTags: (_result, _error, { id }) => [{ id, type: 'User' }],
      query: ({ id, userProfile, seatChangeReview }) => ({
        data: {
          userProfile,
          ...(seatChangeReview
            ? {
                seatChangeReview: {
                  previewId: seatChangeReview.previewId,
                  previewDigest: seatChangeReview.previewDigest,
                  sourceRevision: seatChangeReview.sourceRevision,
                  bufferWaiverAppointmentIds:
                    seatChangeReview.bufferWaiverAppointmentIds,
                },
              }
            : {}),
        },
        method: 'PUT',
        url: `/user_profiles/${id}`,
      }),
    }),

    // User Assessment operations
    updateUserAssessment: builder.mutation<
      UserAssesment,
      { id: string; userAssesment: Partial<UserAssesment> }
    >({
      invalidatesTags: (_result, _error, { id }) => [{ id, type: 'User' }],
      query: ({ id, userAssesment }) => ({
        data: { userAssesment },
        method: 'PUT',
        url: `/user_assesments/${id}`,
      }),
    }),

    // User Employment operations
    getUserEmployment: builder.query<UserEmployment[], { userId: string }>({
      providesTags: (_result, _error, { userId }) => [
        { id: userId, type: 'UserEmployment' },
        'UserEmployment',
      ],
      query: ({ userId }) => ({
        method: 'GET',
        url: `/user_employments?user_id=${userId}`,
      }),
    }),

    createUserEmployment: builder.mutation<
      UserEmployment,
      Partial<UserEmployment>
    >({
      invalidatesTags: ['UserEmployment'],
      query: (userEmployment) => ({
        data: { userEmployment },
        method: 'POST',
        url: '/user_employments',
      }),
    }),

    updateUserEmployment: builder.mutation<
      UserEmployment,
      { id: string; userEmployment: Partial<UserEmployment> }
    >({
      invalidatesTags: ['UserEmployment'],
      query: ({ id, userEmployment }) => ({
        data: { userEmployment },
        method: 'PUT',
        url: `/user_employments/${id}`,
      }),
    }),

    bulkUpdateUserEmployment: builder.mutation<
      UserEmployment[],
      { employments: Partial<UserEmployment[]> }
    >({
      invalidatesTags: ['UserEmployment'],
      query: ({ employments }) => ({
        data: { userEmployment: { employments } },
        method: 'POST',
        url: '/user_employments/bulk',
      }),
    }),

    // User Notifications operations
    updateUserNotifications: builder.mutation<
      UserNotifications,
      { id: string; notifications: Partial<UserNotifications> }
    >({
      invalidatesTags: (_result, _error, { id }) => [{ id, type: 'User' }],
      query: ({ id, notifications }) => ({
        data: { notifications },
        method: 'PATCH',
        url: `/user_notifications/${id}`,
      }),
    }),

    // User roles operations (admin-only)
    getUserRoles: builder.query<Role[], string>({
      query: (userId) => ({
        method: 'GET',
        url: `/users/${userId}/roles`,
      }),
      transformResponse: (response: { roles: Role[] }) => response.roles,
    }),

    addUserRole: builder.mutation<
      Role[],
      {
        userId: string;
        name: string;
        scope: 'global' | 'company';
        companyId?: string;
      }
    >({
      query: ({ userId, name, scope, companyId }) => ({
        method: 'POST',
        url: `/users/${userId}/roles`,
        data: {
          role: {
            name,
            scope,
            ...(scope === 'company' && companyId
              ? { company_id: companyId }
              : {}),
          },
        },
      }),
      transformResponse: (response: { roles: Role[] }) => response.roles,
    }),

    removeUserRole: builder.mutation<
      Role[],
      {
        userId: string;
        name: string;
        scope: 'global' | 'company';
        companyId?: string;
      }
    >({
      query: ({ userId, name, scope, companyId }) => ({
        method: 'DELETE',
        url: `/users/${userId}/roles`,
        data: {
          role: {
            name,
            scope,
            ...(scope === 'company' && companyId
              ? { company_id: companyId }
              : {}),
          },
        },
      }),
      transformResponse: (response: { roles: Role[] }) => response.roles,
    }),

    // The CSV export takes the members table's own params so the file lists the members on screen.
    downloadUsersCSV: builder.mutation<Blob, MembersTableParams>({
      query: (params) => ({
        headers: { Accept: 'text/csv' },
        method: 'GET',
        params: membersTableParams(params),
        responseType: 'blob',
        url: '/users/download_csv',
      }),
    }),
  }),
});

export const {
  usePreviewMemberSeatChangeMutation,
  useGetMemberSeatChangesQuery,
  useGetCurrentUserQuery,
  useGetCurrentUserSessionProbeQuery,
  useLazyGetCurrentUserSessionProbeQuery,
  useGetMembersTableQuery,
  useGetManageCoachesQuery,
  useGetCoachStatusQuery,
  useGetUsersQuery,
  useGetUserQuery,
  useUpdateUserMutation,
  useUpdateCoachStatusMutation,
  useUpdateUserTranscriptSharingMutation,
  useUpdateUserB2BEnabledMutation,
  useGetUserRolesQuery,
  useAddUserRoleMutation,
  useRemoveUserRoleMutation,
  useGetUsersEngagementQuery,
  useGetCoachProfilesQuery,
  useUpdateUserProfileMutation,
  useUpdateUserAssessmentMutation,
  useGetUserEmploymentQuery,
  useCreateUserEmploymentMutation,
  useUpdateUserEmploymentMutation,
  useBulkUpdateUserEmploymentMutation,
  useUpdateUserNotificationsMutation,
  useDownloadUsersCSVMutation,
} = userApi;
